import { useState, useCallback, useEffect } from "react";
import {
  verifyGSTIN,
  getGSTINVerification,
  getVerificationConfig,
  GSTINVerificationResult,
} from "../../data/api/gstinVerificationApi";
import { isValidGstinFormat, namesMatch } from "../../utils/gstinUtils";

type VerificationStatus = "idle" | "verifying" | "verified" | "failed";

interface UseGstinVerificationOptions {
  entityName: string;
  entityType: "vendor" | "customer";
}

interface UseGstinVerificationReturn {
  status: VerificationStatus;
  verificationData: GSTINVerificationResult | null;
  errorMessage: string;
  isVerified: boolean;
  showVerifyButton: boolean;
  nameMismatch: boolean;
  nameMatchConfirmed: boolean;
  verificationRequired: boolean;
  configLoaded: boolean;
  verify: (gstin: string) => Promise<void>;
  checkExisting: (gstin: string) => Promise<void>;
  reset: () => void;
  confirmNameMatch: () => void;
}

export const useGstinVerification = (
  options: UseGstinVerificationOptions
): UseGstinVerificationReturn => {
  const { entityName } = options;

  const [status, setStatus] = useState<VerificationStatus>("idle");
  const [verificationData, setVerificationData] =
    useState<GSTINVerificationResult | null>(null);
  const [errorMessage, setErrorMessage] = useState<string>("");
  const [nameMatchConfirmed, setNameMatchConfirmed] = useState<boolean>(false);
  const [verificationRequired, setVerificationRequired] =
    useState<boolean>(false);
  const [configLoaded, setConfigLoaded] = useState<boolean>(false);
  const [currentGstin, setCurrentGstin] = useState<string>("");

  // On mount, fetch verification config
  useEffect(() => {
    const fetchConfig = async () => {
      try {
        const config = await getVerificationConfig();
        setVerificationRequired(config.required);
      } catch (err: any) {
        console.error("Error fetching verification config:", err);
        // Default to not required if config fetch fails
        setVerificationRequired(false);
      } finally {
        setConfigLoaded(true);
      }
    };

    fetchConfig();
  }, []);

  const isVerified =
    status === "verified" && verificationData?.status === "Active";

  const showVerifyButton =
    isValidGstinFormat(currentGstin) &&
    status !== "verified" &&
    status !== "verifying" &&
    verificationRequired;

  const nameMismatch =
    isVerified &&
    !namesMatch(
      entityName,
      verificationData?.legal_name || "",
      verificationData?.trade_name
    ) &&
    !nameMatchConfirmed;

  const verify = useCallback(async (gstin: string) => {
    setCurrentGstin(gstin);
    setStatus("verifying");
    setErrorMessage("");
    setNameMatchConfirmed(false);

    try {
      const response = await verifyGSTIN(gstin);
      // Backend wraps: { success, data: { verification: {...}, cached }, message }
      const verification = response.data?.verification ?? response.data;
      if (response.success && verification) {
        setVerificationData(verification as GSTINVerificationResult);
        setStatus("verified");
      } else {
        setErrorMessage(response.message || "Verification failed");
        setStatus("failed");
      }
    } catch (err: any) {
      const msg = err?.data?.message || err?.message || "An error occurred during verification";
      setErrorMessage(msg);
      setStatus("failed");
      console.error("Error verifying GSTIN:", err);
    }
  }, []);

  const checkExisting = useCallback(async (gstin: string) => {
    setCurrentGstin(gstin);

    if (!gstin || !isValidGstinFormat(gstin)) {
      return;
    }

    try {
      const response = await getGSTINVerification(gstin);
      // Backend wraps: { success, data: { verification: {...} | null, cached }, message }
      const verification = response.data?.verification;
      if (response.success && verification && verification.gstin) {
        setVerificationData(verification as GSTINVerificationResult);
        setStatus("verified");
      }
      // If verification is null, GSTIN is not yet verified — leave status as idle
    } catch (err: any) {
      // Silently fail for edit forms — verification data may not exist yet
      console.error("Error checking existing GSTIN verification:", err);
    }
  }, []);

  const reset = useCallback(() => {
    setStatus("idle");
    setVerificationData(null);
    setErrorMessage("");
    setNameMatchConfirmed(false);
    setCurrentGstin("");
  }, []);

  const confirmNameMatch = useCallback(() => {
    setNameMatchConfirmed(true);
  }, []);

  return {
    status,
    verificationData,
    errorMessage,
    isVerified,
    showVerifyButton,
    nameMismatch,
    nameMatchConfirmed,
    verificationRequired,
    configLoaded,
    verify,
    checkExisting,
    reset,
    confirmNameMatch,
  };
};
